/**
 * ViewStatePersist — the HUD affordance for the S16 view-channel persist:
 * mounts beside the view picker; visible only when a MODEL view is active AND
 * there is session-ephemeral state to persist. One click writes every dragged
 * placement into the view body as `pin` members (`useViewStatePersist`, one
 * batched mutation). A second button writes changed knob values as a view
 * INSTANCE (`view <Parent> <Name> { knob: value }`, one `upsertInstance` op).
 */
import { useState, type ReactNode } from "react";
import { Button } from "@archlang/ui";
import type { MutationResult } from "../workbench/mutations.js";
import { useViewStatePersist } from "./useViewStatePersist.js";

function describeFailure(r: MutationResult): string {
  if (r.status === "stale") return "compile catching up — try again";
  if (r.status === "refused") return r.message;
  return "not persistable here";
}

export function ViewStatePersist(): ReactNode {
  const api = useViewStatePersist();
  const [note, setNote] = useState<string | null>(null);
  if (!api.viewName || (api.pinCount === 0 && api.changedKnobs.length === 0)) return null;
  return (
    <div className="flex items-center gap-1.5">
      {api.pinCount > 0 && (
        <Button
          variant="secondary"
          size="sm"
          aria-label={`Persist ${api.pinCount} pins to view`}
          title={`Write ${api.pinCount} dragged position(s) into view '${api.viewName}' as pin style members`}
          onClick={() => {
            const r = api.persistPins();
            setNote(r && r.status !== "applied" ? describeFailure(r) : null);
          }}
        >
          Pin {api.pinCount} → {api.viewName}
        </Button>
      )}
      {api.changedKnobs.length > 0 && api.knobPersistSupported && api.knobTargetName && (
        <Button
          variant="secondary"
          size="sm"
          aria-label={`Persist ${api.changedKnobs.length} knob values to view instance`}
          title={`Write ${api.changedKnobs.length} changed knob value(s) into a view instance '${api.knobTargetName}'`}
          onClick={() => {
            const r = api.persistKnobs();
            setNote(r && r.status !== "applied" ? describeFailure(r) : null);
          }}
        >
          Knobs {api.changedKnobs.length} → {api.knobTargetName}
        </Button>
      )}
      {note && (
        <span role="status" className="text-xs text-amber-400">
          {note}
        </span>
      )}
    </div>
  );
}
